import type { GuessedRevenueType } from 'api/v1/transactions/getGuessedDocument';
import { getRevenueOCR } from 'api/v2/revenues';
import { uploadFile } from 'api/v2/upload';
import {
  freqsDict,
  generatePeriods,
} from 'components/molecules/Fields/Period/utils';
import type { Category } from 'components/organisms/CategoriesPicker/categoriesPicker.types';
import { categoryIdWithTaxPeriodToValue } from 'components/organisms/CategoriesPicker/utils';
import { getDefaultVATRate } from 'components/pages/Expenses/Form/utils';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import i18n from 'i18n';
import { useMemo } from 'react';
import { ClientLocationEnum } from 'types/clients.types';
import type { GuessedExpense } from 'types/expense.types';
import { PartialIExpenseV2, VATPeriod } from 'types/expenses.types';
import {
  CurrenciesEnum,
  Period,
  type VATReturnFrequency,
} from 'types/global.types';
import {
  InvoiceStatusEnum,
  InvoiceTypeEnum,
  type Invoice,
  type InvoiceType,
} from 'types/invoice.types';
import { InvoiceFileType } from 'types/revenues.types';
import type { IMatchedItem, Transaction } from 'types/transactions.types';
import {
  AvailableCountries,
  VATTypeEnum,
  type VATType,
} from 'types/users.types';
import { getPeriodFromDate } from 'utils/date';
import formatPeriodLabel from 'utils/formatPeriodLabel';
import { multiplyBy1000 } from 'utils/math';

const categoriesIcons = Object.entries(
  import.meta.glob('../../../../assets/svg/classification/*', { eager: true }),
).reduce((acc, [path, module]) => {
  const key =
    path
      .split('/')
      .pop()
      ?.replace(/\.[^/.]+$/, '') || '';
  return { ...acc, [key]: (module as any).default };
}, {} as Record<string, string>);

const sprinklesClassificationIcons = Object.entries(
  import.meta.glob('../../../../assets/svg/one_click_classification/*', {
    eager: true,
  }),
).reduce((acc, [path, module]) => {
  const key =
    path
      .split('/')
      .pop()
      ?.replace(/\.[^/.]+$/, '') || '';
  return { ...acc, [key]: (module as any).default };
}, {} as Record<string, string>);

const commonLinkingViewProps = {
  searchBarContainerClassName: 'px-6',
  listContainerClassName: 'px-6 overflow-y-auto',
};

export const getTransactionDate = (
  transaction: Transaction,
  format?: string,
): Dayjs | string => {
  const date = dayjs(
    transaction && typeof transaction === 'object'
      ? new Date(transaction.valueDate || transaction.executionDate)
      : new Date(),
  );

  return format ? date.format(format) : date;
};

// Expense creation helpers
const formatExpenseFromTransaction = (
  transaction: Transaction | Transaction[],
  OCRGuess: GuessedExpense,
  country: AvailableCountries,
  globalVATType: VATType,
  globalVATFrequency: VATReturnFrequency,
  category?: Record<string, any>,
  taxPeriod?: VATPeriod,
  isCreditNote?: boolean,
): Omit<PartialIExpenseV2, 'expenseDateNumber' | 'period'> & {
  expenseDateNumber: number;
  period: Period;
} => {
  const isBulk = Array.isArray(transaction);

  const singleTransaction = isBulk ? transaction[0] : transaction;

  const expenseDate = getTransactionDate(
    singleTransaction,
    'YYYY-MM-DD',
  ) as string;

  const supplier = {
    name: OCRGuess?.supplierName || singleTransaction.counterPartyName || '',
  };

  const transactionsAsArray = [transaction].flat();
  const currencySample = transactionsAsArray[0].currency;
  const isSameCurrency = transactionsAsArray.every(
    (t) => t.currency === currencySample,
  );

  const amount = Math.abs(
    transactionsAsArray.reduce((acc, t) => acc + t.amount, 0),
  );

  const baseAmount = Math.abs(
    transactionsAsArray.reduce(
      (acc, t) => acc + (t.baseCurrencyAmount ?? t.amount),
      0,
    ),
  );

  const _category = category || OCRGuess.category;

  const VATRate = getDefaultVATRate({
    isCustomerBE: country === AvailableCountries.belgium,
    expenseDate,
    categorySuggestedVATRate:
      typeof _category?.suggestedVATRate === 'number'
        ? _category.suggestedVATRate
        : undefined,
  });

  const period = getPeriodFromDate(globalVATFrequency, expenseDate);

  const itemCategoryProps = {
    categoryId: _category?.id,
    incomeTaxDeductibility: _category?.incomeTaxDeductibility,
    maxDeductibleVAT: _category?.maxDeductibleVAT,
    professionalPart: OCRGuess.professionalPart || 1,
    category: _category,
  };

  const currency = isSameCurrency ? currencySample : CurrenciesEnum.EUR;
  const isEuroCurrency = currency === CurrenciesEnum.EUR;

  const isInvoice =
    OCRGuess.isInvoice ??
    (country === AvailableCountries.germany &&
      globalVATType === VATTypeEnum.subjectToVAT);

  return {
    expenseDateNumber: +dayjs(expenseDate).format('YYYYMMDD'),
    isCreditNote,
    currency,
    currencyRate: isEuroCurrency ? 1 : amount / baseAmount,
    items: [
      {
        currencyAmount: isEuroCurrency ? baseAmount : amount,
        VATRate,
        VATRegime: OCRGuess.VATRegime ?? undefined,
        vehicle: OCRGuess.vehicle as any,
        ...itemCategoryProps,
      },
    ],
    supplier,
    payments: {
      transactions: transactionsAsArray.map((t) => t._id),
      cash: false,
      other_accounts: false,
    },
    _matchedTransactions: transactionsAsArray,
    period,
    isValidated: true,
    isInvoice,
    taxPeriod,
  };
};

const getPaymentDateAndStatusFromTransaction = (transaction?: Transaction) => {
  const withTransactionDate = transaction?.valueDate;
  const paymentDate = withTransactionDate
    ? dayjs(withTransactionDate).format('YYYY-MM-DD')
    : undefined;
  const status = paymentDate ? InvoiceStatusEnum.paid : InvoiceStatusEnum.sent;

  return {
    paymentDate,
    status,
  };
};

const formatInvoiceFromTransaction = (
  transaction: Transaction | Transaction[],
  category: Record<string, any>,
  type: InvoiceTypeEnum,
  globalVATFrequency: VATReturnFrequency,
  taxPeriod?: any,
  OCRGuess?: GuessedRevenueType,
): Partial<Invoice> => {
  const isBulk = Array.isArray(transaction);

  const singleTransaction = isBulk ? transaction[0] : transaction;

  const unitAmountInclVAT = Math.abs(
    multiplyBy1000(
      isBulk
        ? transaction.reduce(
            (acc, t) => acc + (t.baseCurrencyAmount ?? t.amount),
            0,
          )
        : singleTransaction.baseCurrencyAmount ?? singleTransaction.amount,
    ),
  );

  const items: any[] = [
    {
      unitAmountInclVAT,
      unitAmountExclVAT: 0,
      quantity: 1000,
      name: singleTransaction.counterPartyName,
      unit: 'items',
      category: category || OCRGuess?.category,
      categoryId: category?.id || OCRGuess?.categoryId,
      VATRate: OCRGuess?.VATRate ? OCRGuess?.VATRate * 1000 : undefined,
      doesUnitPriceIncludeVAT: true,
    },
  ];

  const invoiceDate = getTransactionDate(
    singleTransaction,
    'YYYY-MM-DD',
  ) as string;

  const _matchedTransactions = isBulk ? transaction : [transaction];

  const transactions = isBulk
    ? transaction.map((t) => t._id)
    : [transaction._id];

  const period = getPeriodFromDate(
    globalVATFrequency as VATReturnFrequency,
    invoiceDate,
  );

  return {
    currency: OCRGuess?.currency || 'EUR',
    invoiceDate,
    dueDate: invoiceDate,
    paymentDate: invoiceDate,
    items,
    status: InvoiceStatusEnum.paid,
    transactions,
    _matchedTransactions,
    type,
    taxPeriod,
    period,
    client:
      type === InvoiceTypeEnum.OTHER_REVENUE
        ? ({
            location: ClientLocationEnum.local,
            name: OCRGuess?.client?.name || undefined,
          } as any)
        : OCRGuess?.client,
    fileType: 'generated',
  };
};

const getBaseInvoiceData = (
  OCRGuess?: GuessedRevenueType,
): Partial<Invoice> => ({
  type: InvoiceTypeEnum.INVOICE,
  transactions: [],
  _matchedTransactions: [],
  status: 'sent',
  currency: OCRGuess?.currency || CurrenciesEnum.EUR,
});

const adaptRevenueOCRResult = (
  result: Partial<Invoice>,
  transactions?: Transaction[],
): Partial<Invoice> => {
  return {
    ...getBaseInvoiceData(),
    ...result,
    ...getPaymentDateAndStatusFromTransaction(transactions?.[0]),
    dueDate:
      result.dueDate || result.invoiceDate
        ? dayjs(result.invoiceDate).add(1, 'day').format('YYYY-MM-DD')
        : undefined,
    ...(transactions
      ? {
          transactions: transactions.map((t) => t._id),
          _matchedTransactions: transactions,
        }
      : null),
  };
};

const getRevenueOCRFromFile = (
  file: File,
  revenueType: InvoiceType,
  onProgress: (n: number) => void,
) => {
  return uploadFile({ file, onProgress }).then(({ key }: any) =>
    getRevenueOCR({ filename: key.split('/').pop(), type: revenueType }).then(
      (result) => ({
        filePath: key,
        files: [{ path: key }],
        fileType: InvoiceFileType.imported,
        ...result,
      }),
    ),
  );
};

export const useMapIdsWithTransactions = (
  ids: string[] = [],
  transactions: Transaction[] = [],
): Transaction[] => {
  return useMemo(() => {
    const list = (transactions || []) as Transaction[];
    return list.filter((t) => (ids || []).includes(t._id));
  }, [ids, transactions]);
};

type UseTransactionsTypeStateReturn = {
  isBulk: boolean;
  isPositive: boolean;
  isNegative: boolean;
  isMixed: boolean;
  isPostiveMoreThanNegative: boolean;
  isEmpty: boolean;
  isForeign: boolean;
};

export const useTransactionsTypeState = (
  transactions: Transaction | Transaction[],
): UseTransactionsTypeStateReturn => {
  return useMemo(() => {
    const isBulk = Array.isArray(transactions);
    if (!isBulk || (isBulk && transactions.length == 1)) {
      const _transaction = isBulk ? transactions[0] : transactions;
      const isPositive = _transaction.amount > 0;
      return {
        isEmpty: false,
        isBulk: false,
        isPositive,
        isNegative: !isPositive,
        isMixed: false,
        isPostiveMoreThanNegative: isPositive,
        isForeign: _transaction.currency !== 'EUR',
      };
    }
    if (!transactions.length) {
      return {
        isEmpty: true,
        isBulk: false,
        isPositive: false,
        isNegative: false,
        isMixed: false,
        isPostiveMoreThanNegative: false,
        isForeign: false,
      };
    }
    const positiveDocuments = transactions.filter((d) => d.amount > 0);
    const negativeDocuments = transactions.filter((d) => d.amount < 0);
    const isPositive = positiveDocuments.length === transactions.length;
    const isNegative = negativeDocuments.length === transactions.length;
    const isPostiveMoreThanNegative =
      positiveDocuments.length > negativeDocuments.length;
    return {
      isEmpty: false,
      isBulk: true,
      isPositive,
      isNegative,
      isMixed: !isPositive && !isNegative,
      isPostiveMoreThanNegative,
      isForeign: transactions.some((t) => t.currency !== 'EUR'),
    };
  }, [transactions]);
};

const isTypeExpense = (type: string) => {
  return type === 'expense';
};

const isTypeInvoice = (type: string) => {
  return ['invoice', 'credit-note', 'other-revenue'].includes(type);
};

const matchedItemSubmitKey = {
  expense: 'expenseId',
  invoice: 'revenueId',
};

const getBulkDocUpdateKey = (type: string) => {
  return isTypeExpense(type)
    ? matchedItemSubmitKey.expense
    : matchedItemSubmitKey.invoice;
};

const adaptTransactionToLink = (transaction: Transaction) => {
  // We have to include the "amount" to be part of the "matchedItems" to calculate the linking progress
  const matchedItemsWithAmounts = transaction.matchedItems?.map((item) => {
    const _item = transaction.matchedItemsDetails?.find(
      (_item) => _item._id === item.documentId,
    );

    const amount = _item?.baseCurrencyTotalAmountInclVAT
      ? _item?.baseCurrencyTotalAmountInclVAT / 1000
      : (_item?.baseCurrencyAmount || 0) * (_item?.isCreditNote ? -1 : 1);

    return { ...item, amount };
  });

  return { ...transaction, matchedItems: matchedItemsWithAmounts };
};

const createExpenseMatchedItem = (
  _id: string,
  isCreditNote: boolean,
): IMatchedItem => {
  return {
    type: 'expense',
    documentId: _id,
    isCreditNote,
  };
};

const createInvoiceMatchedItem = (
  _id: string,
  isCreditNote: boolean,
): IMatchedItem => {
  return {
    type: 'invoice',
    documentId: _id,
    isCreditNote,
  };
};

const freqToCategory = (
  frequency: VATReturnFrequency,
  question: string,
  categoryId: string,
): Category => {
  const periods = generatePeriods(frequency, dayjs().endOf('year'));
  return {
    id: frequency,
    title: i18n.t(`select_${freqsDict[frequency]}`),
    question,
    compact: true,
    list: periods.map((period) => ({
      title: formatPeriodLabel(period) as string,
      id: categoryIdWithTaxPeriodToValue(categoryId, frequency, period),
      parentCatgoryId: categoryId,
      taxPeriod: { ...period, frequency },
      frequency,
    })),
  };
};

export const createPeriodCategory = (
  categoryId: string,
  freqs: VATReturnFrequency[],
  title: string,
  question: string,
): Category => {
  const hasOnlyOneFreq = freqs.length === 1;
  return {
    id: categoryId,
    title,
    question,
    compact: hasOnlyOneFreq,
    list: hasOnlyOneFreq
      ? freqToCategory(freqs[0], question, categoryId).list
      : freqs.flatMap((freq) => freqToCategory(freq, question, categoryId)),
  };
};

export {
  formatExpenseFromTransaction,
  formatInvoiceFromTransaction,
  getBaseInvoiceData,
  categoriesIcons,
  sprinklesClassificationIcons,
  getRevenueOCRFromFile,
  adaptRevenueOCRResult,
  isTypeExpense,
  isTypeInvoice,
  getBulkDocUpdateKey,
  createExpenseMatchedItem,
  createInvoiceMatchedItem,
  adaptTransactionToLink,
  commonLinkingViewProps,
};
